'use client';

import Link from 'next/link';
import Image from 'next/image';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';

import { CustomSelect } from '@/slw/shared/ui/custom/select';
import { PageChangeDataProps, Pagination } from '@/slw/widgets/common/pagination';
import { DealerItem as DealerItemType } from '@/slw/entities/buyer/available-dealer/available-dealer.type';
import { ResponseMeta } from '@/slw/shared/types/api.type';
import { formatNumber } from '@/slw/shared/lib/format-number';

interface DealersProps {
  dealers: DealerItemType[];
  metaData: ResponseMeta;
  isLogged: boolean;
  sort: 'asc' | 'desc';
  slug: string[];
}

export function Dealers({ dealers, metaData, isLogged, sort, slug }: DealersProps) {
  const dropdownOptions = [
    { value: 'asc', label: 'A-Z' },
    { value: 'desc', label: 'Z-A' },
  ];
  const router = useRouter();

  const onPageChange = ({ page }: PageChangeDataProps) => {
    let url = `/available-dealer/${slug[0]}`;

    if (slug[1]) {
      url = `${url}/${slug[1]}`;
    }

    router.replace(`${url}?page=${page}&sort=${sort}`);
  };

  const onPageSort = (value: 'asc' | 'desc' | string) => {
    let url = `/available-dealer/${slug[0]}`;

    if (slug[1]) {
      url = `${url}/${slug[1]}`;
    }

    router.replace(`${url}?page=${metaData?.pageIndex ?? 1}&sort=${value}`);
  };

  // Calculate summary range
  const totalCount = metaData?.totalItems ?? 0;
  const pageSize = metaData?.pageSize ?? 21;
  const pageIndex = metaData?.pageIndex ?? 1;
  const start = totalCount === 0 ? 0 : (Number(pageIndex) - 1) * pageSize + 1;
  const end = Math.min(Number(pageIndex) * pageSize, totalCount);

  return (
    <>
      <div className='showing flex items-center justify-between mb-8'>
        <div className='text-sm'>{`Showing ${start}–${end} of ${formatNumber(totalCount)} results`}</div>
        <div className='flex items-center gap-5'>
          <div className='text-sm'>Sort By</div>
          <div>
            <CustomSelect className='w-10' options={dropdownOptions} value={sort} onChange={onPageSort} />
          </div>
        </div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
        {dealers.map((dealer) => (
          <div key={dealer.id} className='bg-sidebar rounded-md p-4'>
            <div className='font-bold border-b border-[#EBE9E5] pb-2 mb-2'>{dealer.fullName}</div>

            {isLogged && (
              <>
                <div className='text-sm flex items-center mb-2 gap-2'>
                  <img src='/images/icon/icon-maker.png' alt='' />
                  {dealer.address}
                </div>
                <div className='text-sm flex items-center mb-2 gap-2'>
                  <img src='/images/icon/icon-envelop.png' alt='' /> {dealer.email}
                </div>
                <div className='text-sm flex items-center mb-2 gap-2'>
                  <img src='/images/icon/icon-phone.png' alt='' /> {dealer.preferredContactPhoneNumber}
                </div>
                <div className='text-sm flex items-center gap-2'>
                  <img src='/images/icon/icon-link.png' alt='' />
                  {dealer.website}
                </div>
              </>
            )}
            <div className='inline-block'>
              <Link
                href={`/dealer-listings/${dealer.id}`}
                className={clsx(
                  `relative font-bold rounded-[6px] bg-primary text-background uppercase
              flex justify-start items-center gap-4 md:gap-10 px-4 md:px-5 py-2 md:py-[0.75rem]
              hover:text-background! hover:bg-primary-3`,
                  {
                    'mt-4 md:mt-5': isLogged,
                    'mt-1': !isLogged,
                  },
                )}
              >
                <span>View Listing</span>
                <Image src='/images/icon/plus-white.png' width={17} height={17} alt='' />
              </Link>
            </div>
          </div>
        ))}
      </div>

      {!!dealers && !!dealers.length && (
        <div className='mt-8'>
          <Pagination currentPage={pageIndex} totalItems={totalCount} pageSize={pageSize} onPageChange={onPageChange} />
        </div>
      )}
    </>
  );
}
